import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageSkeleton } from "@/components/PageSkeleton";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { ClipboardCheck, Check, X } from "lucide-react";

const STATUS_BADGE = {
  Menunggu: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
  Disetujui: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
  Ditolak: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300",
};

export default function AttendanceRequests() {
  const [rows, setRows] = useState(null);
  const [statusF, setStatusF] = useState("Menunggu");
  const [decide, setDecide] = useState(null); // {req, decision}
  const [note, setNote] = useState("");

  const load = () => {
    const q = statusF !== "all" ? `?status=${statusF}` : "";
    api.get(`/attendance/requests${q}`).then((r) => setRows(r.data.rows)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF]);

  const submitDecision = async () => {
    try {
      await api.post(`/attendance/requests/${decide.req._id}/decide`, { decision: decide.decision, note });
      toast.success(decide.decision === "approve" ? "Pengajuan disetujui" : "Pengajuan ditolak");
      setDecide(null); setNote(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!rows) return <PageSkeleton stats={0} rows={6} />;

  return (
    <div className="space-y-4" data-testid="attendance-requests-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-[#C9A227]" /> Persetujuan Izin & Koreksi</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Setujui atau tolak pengajuan izin/koreksi absensi karyawan & admin.</p>
        </div>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="w-40" data-testid="req-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Menunggu">Menunggu</SelectItem>
            <SelectItem value="Disetujui">Disetujui</SelectItem>
            <SelectItem value="Ditolak">Ditolak</SelectItem>
            <SelectItem value="all">Semua</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="p-4">
        <div className="space-y-1.5" data-testid="req-list">
          {rows.length === 0 && <p className="text-sm text-muted-foreground py-4">Tidak ada pengajuan.</p>}
          {rows.map((r) => (
            <div key={r._id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2.5 text-sm" data-testid={`req-row-${r._id}`}>
              <div className="min-w-0">
                <p className="font-medium truncate">{r.user_name} <span className="text-[10px] text-muted-foreground uppercase">({r.role})</span> · <span className="capitalize">{r.type}</span>{r.leave_type ? ` · ${r.leave_type}` : ""}</p>
                <p className="text-[11px] text-muted-foreground">{formatDate(r.date)}{r.proposed_check_in ? ` · usul masuk ${r.proposed_check_in}` : ""}{r.proposed_check_out ? ` · usul pulang ${r.proposed_check_out}` : ""}</p>
                <p className="text-[11px] text-muted-foreground truncate">{r.reason}{r.decision_note ? ` — ${r.decided_by}: ${r.decision_note}` : ""}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {r.status === "Menunggu" ? (
                  <>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" onClick={() => { setDecide({ req: r, decision: "approve" }); setNote(""); }} data-testid={`req-approve-${r._id}`}><Check className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => { setDecide({ req: r, decision: "reject" }); setNote(""); }} data-testid={`req-reject-${r._id}`}><X className="h-4 w-4" /></Button>
                  </>
                ) : <Badge className={`text-[10px] ${STATUS_BADGE[r.status] || ""}`}>{r.status}</Badge>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={!!decide} onOpenChange={(o) => { if (!o) setDecide(null); }}>
        <DialogContent className="sm:max-w-sm" data-testid="req-decide-dialog">
          <DialogHeader><DialogTitle className="font-heading">{decide?.decision === "approve" ? "Setujui Pengajuan" : "Tolak Pengajuan"}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">{decide?.req?.user_name} · {decide?.req?.type} · {decide && formatDate(decide.req.date)}</p>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan keputusan (opsional)" data-testid="req-decide-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecide(null)}>Batal</Button>
            <Button variant={decide?.decision === "approve" ? "gold" : "destructive"} onClick={submitDecision} data-testid="req-decide-submit">{decide?.decision === "approve" ? "Setujui" : "Tolak"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
